/**
 * Lobby — admin or player view of the waiting room.
 *
 * Wires the connect/disconnect lifecycle: on mount we open the WebSocket
 * (`useLobbyStore.connect(sessionId)`), and on unmount we close it. The
 * admin role auto-issues a `joinSession` with a default name + colour
 * once the `Hello` arrives so their slot appears in the roster without
 * requiring them to walk through the JoinFlow stepper they don't need.
 *
 * Player role renders the JoinFlow stepper until their slot exists in
 * the server-pushed roster, then swaps to PlayerWaitingRoom.
 *
 * When the server transitions `phase` to `'playing'` (admin clicks BEGIN
 * MATCH and the server validates ≥2 ready), every connected client
 * navigates to `/play`.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { surfaces, fontStacks } from '@qd/design-tokens';
import { asSessionId, type PlayerColorKey } from '@qd/protocol';
import { SlateBackground } from '../components/base/SlateBackground';
import { AdminLobbyView } from '../components/lobby/AdminLobbyView';
import { JoinFlow } from '../components/lobby/JoinFlow';
import { PlayerWaitingRoom } from '../components/lobby/PlayerWaitingRoom';
import { ReconnectOverlay } from '../components/lobby/ReconnectOverlay';
import { useLocation } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  useIsAdmin,
  useLobbyPhase,
  useLobbyStore,
  useSelfPlayer,
  useTakenColors,
} from '../stores/lobbyStore';

/** Default colour given to the auto-joined admin. Falls back to a free
 *  pigment if `prussian` is somehow already taken (it shouldn't be on a
 *  first-connect to a brand-new room, but reconnects after a colour
 *  change should not crash). */
const ADMIN_DEFAULT_COLOR: PlayerColorKey = 'prussian';

export function Lobby() {
  const { sessionId: routeSessionId = '' } = useParams<{ sessionId: string }>();
  const [search] = useSearchParams();
  const role = search.get('role') === 'admin' ? 'admin' : 'player';
  const navigate = useNavigate();

  const connect = useLobbyStore((s) => s.connect);
  const disconnect = useLobbyStore((s) => s.disconnect);
  const joinSession = useLobbyStore((s) => s.joinSession);
  const toggleReady = useLobbyStore((s) => s.toggleReady);
  const lobbyState = useLobbyStore((s) => s.lobbyState);
  const selfId = useLobbyStore((s) => s.selfId);
  const phase = useLobbyPhase();
  const isAdmin = useIsAdmin();
  const selfPlayer = useSelfPlayer();
  const takenColors = useTakenColors();
  const location = useLocation();
  const updateSettings = useLobbyStore((s) => s.updateSettings);
  const isMobile = useIsMobile();

  // Open / close the WebSocket on mount/unmount.
  useEffect(() => {
    if (!routeSessionId) return;
    connect(asSessionId(routeSessionId));
    return () => {
      disconnect();
    };
    // We deliberately only react to a change in routeSessionId, not the
    // function references (zustand returns stable refs anyway).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeSessionId]);

  // Admin auto-join: the moment we have a `selfId` (handshake complete)
  // and a lobbyState (so we know who else is in the room), seat the host
  // and mark them ready. Done exactly once per connection.
  const adminJoinedRef = useRef(false);
  useEffect(() => {
    if (role !== 'admin') return;
    if (!selfId || !lobbyState) return;
    if (adminJoinedRef.current) return;
    // If we're already in the roster (reconnect) skip.
    const alreadySeated = lobbyState.players.some((p) => p.id === selfId);
    if (alreadySeated) {
      adminJoinedRef.current = true;
      return;
    }
    const color = takenColors.has(ADMIN_DEFAULT_COLOR)
      ? findFreeColor(takenColors)
      : ADMIN_DEFAULT_COLOR;
    if (!color) return;
    // Default the admin to a generic player-slot name. We deliberately
    // do NOT use a role label like "Host" or "Lead Tester" — once the
    // match starts, the bottom-of-screen mobile action bar shows
    // "WAITING FOR {currentPlayerName}…" when it's not your turn, and a
    // role-styled label there reads as a vestigial lobby message ("the
    // host hasn't started yet"). A neutral player-slot label keeps that
    // caption unambiguous as a turn indicator.
    joinSession('Player 1', color);
    toggleReady();

    // Apply settings if we just came from the Create route.
    const settings = (location.state as any)?.settings;
    if (settings) {
      const turnTimerMap = { off: 0, '60s': 60, '120s': 120 };
      updateSettings({
        difficulty: settings.difficulty,
        turnTimer: turnTimerMap[settings.turnTimer as keyof typeof turnTimerMap] ?? 60,
      });
    }

    adminJoinedRef.current = true;
  }, [role, selfId, lobbyState, takenColors, joinSession, toggleReady, location.state, updateSettings]);

  // Watch for phase transition → /play.
  useEffect(() => {
    if (phase === 'playing' && routeSessionId) {
      navigate(`/game/${routeSessionId}/play`);
    }
  }, [phase, routeSessionId, navigate]);

  return (
    <SlateBackground particles>
      <div
        style={{
          position: 'relative',
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <main
          style={{
            flex: 1,
            padding: isMobile ? '16px 16px 96px' : '32px 32px 96px',
          }}
        >
          {role === 'admin' || isAdmin ? (
            <AdminLobbyView sessionId={routeSessionId} />
          ) : selfPlayer ? (
            <PlayerWaitingRoom sessionId={routeSessionId} />
          ) : (
            <JoinFlowShell sessionId={routeSessionId} />
          )}
        </main>
        <SessionCodeFooter code={routeSessionId} sessionId={routeSessionId} />
      </div>
      <ReconnectOverlay />
    </SlateBackground>
  );
}

function JoinFlowShell({ sessionId }: { sessionId: string }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
      }}
    >
      <JoinFlow sessionId={sessionId} />
    </div>
  );
}

function findFreeColor(taken: ReadonlySet<PlayerColorKey>): PlayerColorKey | null {
  const all: PlayerColorKey[] = ['prussian', 'vermillion', 'mustard', 'verdigris', 'tyrian', 'bone'];
  for (const c of all) if (!taken.has(c)) return c;
  return null;
}

interface SessionCodeFooterProps {
  code: string;
  sessionId: string;
}

function SessionCodeFooter({ code, sessionId }: SessionCodeFooterProps) {
  const [copied, setCopied] = useState(false);
  const isMobile = useIsMobile();

  async function handleCopy() {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard permission denied — silent fallback.
    }
  }

  // On mobile we stack the SESSION label above the copy button with centred
  // alignment — at 375px wide the desktop space-between layout pushes the
  // code label and the copy button so far apart that the copy target is
  // hard to tap (and the letter-spacing on the code makes overflow likely).
  return (
    <footer
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        padding: isMobile ? '12px 16px' : '16px 32px',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: isMobile ? 'center' : 'space-between',
        alignItems: 'center',
        gap: isMobile ? 4 : 0,
        fontFamily: fontStacks.mono,
        color: surfaces.chalk,
        fontSize: isMobile ? 12 : 14,
        letterSpacing: '0.18em',
        opacity: 0.85,
      }}
    >
      <span style={{ opacity: 0.55 }}>SESSION · {sessionId.slice(0, 14)}</span>
      <button
        type="button"
        onClick={handleCopy}
        style={{
          background: 'none',
          border: 0,
          color: surfaces.chalk,
          fontFamily: fontStacks.mono,
          fontSize: isMobile ? 14 : 18,
          letterSpacing: isMobile ? '0.18em' : '0.32em',
          cursor: code ? 'pointer' : 'default',
          padding: isMobile ? '4px 8px' : 0,
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {code ? `${code} ${copied ? '· COPIED' : '· COPY'}` : '— — —'}
      </button>
    </footer>
  );
}
